import { jsPDF } from "jspdf";
import { formatDate } from "./format";

const val = (x) => (x == null ? "" : String(x));
const dob = (g, m, a) => (g || m || a)
  ? [String(g || "").padStart(2, "0"), String(m || "").padStart(2, "0"), a].filter(Boolean).join("/")
  : "";
const join = (arr) => arr.filter(Boolean).join(" ");
const isoToDisplay = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(iso);
  const gg = String(d.getDate()).padStart(2, "0"), mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${gg}/${mm}/${d.getFullYear()}`;
};

const pick = (...xs) => { for (const x of xs) { if (x != null && String(x).trim() !== "") return x; } return ""; };

// Normalizza un soggetto (assistito o controparte, con retrocompatibilità sui campi storici).
// Il veicolo può essere inline (costruttore/modello/targa) oppure in un sotto-oggetto veicolo/veicoli[0].
function norm(s = {}) {
  const v = s.veicolo || (Array.isArray(s.veicoli) && s.veicoli[0]) || {};
  const isPatente = String(s.documento || "").toLowerCase().includes("patente");
  return {
    cognome: pick(s.cognome, s.ragione_sociale),
    nome: s.nome || "",
    cf: pick(s.codice_fiscale, s.partita_iva, s.piva),
    indirizzo: s.indirizzo || "",
    cap: s.cap || "",
    comune: s.comune || "",
    provincia: s.provincia || "",
    stato: s.stato || (s.veicolo_estero === "SI" || s.veicolo_estero === true ? "ESTERO" : "ITALIA"),
    tel_no_cai: !!s.tel_no_cai,
    telefono: s.tel_no_cai ? "" : pick(s.telefono, s.cell, s.cellulare, s.tel_casa, s.tel_ufficio),
    email: s.email || "",
    nascita: pick(dob(s.nasc_g, s.nasc_m, s.nasc_a), isoToDisplay(s.data_nascita)),
    patente_numero: pick(s.patente_numero, isPatente ? s.numero_doc : ""),
    patente_categoria: pick(s.patente_categoria, isPatente ? s.categoria_patente : ""),
    patente_scadenza: pick(dob(s.patente_scad_g, s.patente_scad_m, s.patente_scad_a), isPatente ? isoToDisplay(s.data_scadenza_doc) : ""),
    assicurazione: pick(s.assicurazione, s.compagnia, v.compagnia),
    agenzia: pick(s.agenzia, s.ispettorato),
    polizza: pick(s.polizza_n, s.polizza, s.numero_polizza, v.numero_polizza),
    polizza_scad: pick(dob(s.scad_g, s.scad_m, s.scad_a), isoToDisplay(s.scadenza_polizza), isoToDisplay(v.scadenza_polizza)),
    targa: pick(s.targa, v.targa),
    marca: join([pick(s.costruttore, s.marca, v.marca), pick(s.modello, v.modello)]),
  };
}

export const CIRCOSTANZE = [
  "in fermata / in sosta",
  "ripartiva dopo una sosta / apriva una portiera",
  "stava parcheggiando",
  "usciva da un parcheggio, da un luogo privato, da una strada vicinale",
  "entrava in un parcheggio, in un luogo privato, in una strada vicinale",
  "si immetteva in una piazza a senso rotatorio",
  "circolava su una piazza a senso rotatorio",
  "tamponava procedendo nello stesso senso e nella stessa fila",
  "procedeva nello stesso senso, ma in una fila diversa",
  "cambiava fila",
  "sorpassava",
  "girava a destra",
  "girava a sinistra",
  "retrocedeva",
  "invadeva la sede stradale riservata alla circolazione in senso inverso",
  "proveniva da destra",
  "non aveva osservato il segnale di precedenza o di semaforo rosso",
];

// Costruisce lo stato editabile del form CAI/CID precompilato dai dati della pratica.
export function praticaToCaiForm(p = {}) {
  const schedaA = p.assistito_scheda || {};
  const assA = (p.assistiti && p.assistiti[0]) || {};
  const srcA = {
    ...assA, ...schedaA,
    cognome: pick(schedaA.cognome, assA.cognome),
    ragione_sociale: pick(schedaA.ragione_sociale, p.assistito_ragione_sociale, p.nome_assistito, assA.ragione_sociale),
    nome: pick(schedaA.nome, assA.nome),
    codice_fiscale: pick(schedaA.codice_fiscale, p.codice_fiscale, assA.codice_fiscale),
    targa: pick(schedaA.targa, assA.targa, p.targa),
    compagnia: pick(schedaA.compagnia, assA.compagnia, p.compagnia),
    assicurazione: pick(schedaA.assicurazione, schedaA.compagnia, assA.assicurazione, assA.compagnia, p.compagnia),
    ispettorato: pick(schedaA.ispettorato, assA.ispettorato, p.assistito_ispettorato),
  };
  const A = norm(srcA);
  const B = norm((p.controparti && p.controparti[0]) || {});
  const roleA = pick(p.assistito_ruolo, schedaA.ruolo, assA.ruolo);
  const roleB = pick(((p.controparti && p.controparti[0]) || {}).ruolo);

  // Ruoli per i quali il soggetto coincide con il conducente: in assenza di un
  // conducente separato si usano i dati del soggetto nella sezione 9 del CAI.
  const soggettoEConducente = (role) =>
    { const r = String(role || "").trim().toLowerCase(); return r.includes("conducente") && !r.includes("non conducente"); };

  // Sezione 9 (conducente) del CAI. Priorità ai dati del conducente separato
  // salvato nella pratica (cond.is_cliente === false). Altrimenti si usano i
  // dati del soggetto solo se il ruolo è "Proprietario Conducente"/"Conducente",
  // in caso contrario la sezione resta vuota.
  const condSection = (S, cond, role) => {
    if (cond && typeof cond === "object" && cond.is_cliente === false) {
      return {
        cond_cognome: cond.cognome || "",
        cond_nome: cond.nome || "",
        cond_nascita: isoToDisplay(cond.data_nascita),
        cond_cf: cond.codice_fiscale || "",
        cond_indirizzo: join([cond.indirizzo, cond.numero_civico, cond.cap, cond.comune, cond.provincia]),
        cond_stato: "",
        cond_tel: pick(cond.cellulare, cond.email),
        patente: cond.patente_n || "",
        categoria: cond.patente_categoria || "",
        patente_scad: isoToDisplay(cond.patente_scadenza),
      };
    }
    if (soggettoEConducente(role)) {
      return {
        cond_cognome: S.cognome, cond_nome: S.nome, cond_nascita: S.nascita, cond_cf: S.cf,
        cond_indirizzo: join([S.indirizzo, S.cap, S.comune, S.provincia]), cond_stato: S.stato,
        cond_tel: S.tel_no_cai ? "" : (S.telefono || S.email),
        patente: S.patente_numero, categoria: S.patente_categoria, patente_scad: S.patente_scadenza,
      };
    }
    return {
      cond_cognome: "", cond_nome: "", cond_nascita: "", cond_cf: "",
      cond_indirizzo: "", cond_stato: "", cond_tel: "",
      patente: "", categoria: "", patente_scad: "",
    };
  };

  const veic = (S, oss, cond, role) => ({
    cognome: S.cognome, nome: S.nome, cf: S.cf,
    indirizzo: join([S.indirizzo, S.cap, S.comune, S.provincia]), cap: S.cap, stato: S.stato,
    tel: S.tel_no_cai ? "" : (S.telefono || S.email),
    marca: S.marca, targa: S.targa, stato_imm: S.stato,
    ass_denominazione: S.assicurazione, polizza: S.polizza, carta_verde: "",
    valido_dal: "", valido_al: S.polizza_scad, agenzia: S.agenzia,
    ag_indirizzo: "", ag_stato: "", ag_tel: "", polizza_copre: "",
    ...condSection(S, cond, role),
    danni_visibili: "", osservazioni: oss || "",
  });
  const ferito = (src, role) => {
    if (!String(role || "").toLowerCase().includes("ferit")) return { tipo: "", nome: "", indirizzo: "", nascita: "", cf: "", ricovero: "", ps: "" };
    const s = src || {};
    const r = String(role).toLowerCase();
    return {
      tipo: r.includes("conducente") ? "conducente" : r.includes("passegger") || r.includes("trasportat") ? "passeggero" : r.includes("pedone") ? "pedone" : "",
      nome: join([s.nome, s.cognome]) || s.ragione_sociale || "",
      indirizzo: join([s.comune, s.indirizzo, s.numero_civico]),
      nascita: join([pick(s.comune_nascita, s.luogo_nascita), pick(dob(s.nasc_g, s.nasc_m, s.nasc_a), isoToDisplay(s.data_nascita))]),
      cf: s.codice_fiscale || "",
      ricovero: s.ps_ricovero || "",
      ps: [s.ps_nome, s.ps_citta].filter(Boolean).join(" di "),
    };
  };
  const emptyFerito = () => ({ tipo: "", nome: "", indirizzo: "", nascita: "", cf: "", ricovero: "", ps: "" });
  const foglio2 = (src, role) => ({
    testimoni: [{ nome: "", cf: "", indirizzo: "", tel: "" }, { nome: "", cf: "", indirizzo: "", tel: "" }],
    proprietario: { nome: "", indirizzo: "", cap: "", provincia: "", nascita: "", cf: "" },
    feriti: [ferito(src, role), emptyFerito()],
  });
  const srcB = (p.controparti && p.controparti[0]) || {};
  const f2A = foglio2(srcA, roleA), f2B = foglio2(srcB, roleB);
  const anyFerito = !!(f2A.feriti[0].tipo || f2B.feriti[0].tipo);
  return {
    numero_pratica: p.numero_pratica || "",
    sinistro: {
      data: isoToDisplay(p.data_sinistro), ora: p.ora_sinistro || "",
      luogo: join([p.luogo_sinistro, p.indirizzo_sinistro]),
      feriti: anyFerito ? "si" : "", danni_veicoli: "", danni_oggetti: "",
      // Testimoni: solo il nominativo (cognome e nome), senza indirizzo/telefono o altri dati.
      testimoni: (Array.isArray(p.testimoni) ? p.testimoni : [])
        .map((t) => ((t && (t.nominativo || [t.cognome, t.nome].filter(Boolean).join(" "))) || "").trim())
        .filter(Boolean)
        .join(", "),
    },
    dinamica: val(p.descrizione_sinistro || p.dinamica || p.note),
    dinamica_font: 14,
    A: { ...veic(A, "", p.conducente, roleA), foglio2: f2A },
    B: { ...veic(B, "", p.controparte_conducente, roleB), foglio2: f2B },
    circostanze: CIRCOSTANZE.map((t) => ({ t, a: false, b: false })),
    croquis: "",
    altre: { autorita: "", legge39: "", cc: false, ps: false, vvuu: false },
  };
}

const CYAN = [41, 171, 226], CYAN_T = [223, 240, 250];
const YELLOW = [255, 242, 0], YELLOW_T = [251, 251, 220];

// Avvolge il doc jsPDF scalando e traslando tutto il disegno: così il layout del
// modulo CAI occupa al massimo il foglio A4 (margini ridotti) senza fuoriuscire.
// Le misure orizzontali/verticali e la dimensione del testo vengono scalate in modo
// coerente; getTextWidth/splitTextToSize restano consistenti con lo spazio di progetto.
function _scaleDoc(doc, k, tx, ty) {
  const SX = (v) => v * k + tx, SY = (v) => v * k + ty, SL = (v) => v * k;
  return new Proxy(doc, {
    get(t, p) {
      switch (p) {
        case "text": return (txt, x, y, o) => { const oo = o ? { ...o } : o; if (oo && typeof oo.maxWidth === "number") oo.maxWidth = SL(oo.maxWidth); return t.text(txt, SX(x), SY(y), oo); };
        case "rect": return (x, y, w, h, s) => t.rect(SX(x), SY(y), SL(w), SL(h), s);
        case "roundedRect": return (x, y, w, h, rx, ry, s) => t.roundedRect(SX(x), SY(y), SL(w), SL(h), SL(rx), SL(ry), s);
        case "line": return (x1, y1, x2, y2) => t.line(SX(x1), SY(y1), SX(x2), SY(y2));
        case "addImage": return (d, f, x, y, w, h, ...r) => t.addImage(d, f, SX(x), SY(y), SL(w), SL(h), ...r);
        case "setFontSize": return (s) => t.setFontSize(s * k);
        case "setLineWidth": return (w) => t.setLineWidth(SL(w));
        case "splitTextToSize": return (txt, w, ...r) => t.splitTextToSize(txt, SL(w), ...r);
        case "getTextWidth": return (txt) => t.getTextWidth(txt) / k;
        // Modulo CAI: tutto il testo sempre in grassetto e nero pieno per massima nitidezza di stampa.
        case "setFont": return (family) => t.setFont(family || "helvetica", "bold");
        case "setTextColor": return () => t.setTextColor(0, 0, 0);
        default: { const v = t[p]; return typeof v === "function" ? v.bind(t) : v; }
      }
    },
  });
}

// ---------------------------------------------------------------------------
// Modulo ufficiale (3 colonne, 2 pagine) generato dal FORM editabile.
// form: output di praticaToCaiForm, eventualmente modificato dall'utente.
// opts: { intestazione: boolean }
// ---------------------------------------------------------------------------
export function buildCaiCidUfficiale(form = {}, studio = {}, opts = {}) {
  const withHeader = opts.intestazione !== false;
  const _doc = new jsPDF({ unit: "mm", format: "a4" });
  // Ingrandimento del layout per riempire al massimo l'A4 (margini stretti) senza overflow.
  const _BX0 = 8, _BX1 = 202, _BY0 = 9, _BY1 = 291; // bounding box del contenuto (mm, invariato)
  const _mar = 5, _marTop = 4, _marBot = 3;
  const _k = Math.min((210 - 2 * _mar) / (_BX1 - _BX0), (297 - _marTop - _marBot) / (_BY1 - _BY0));
  const doc = _scaleDoc(_doc, _k, _mar - _BX0 * _k, _marTop - _BY0 * _k);
  const W = 210, H = 297, M = 8, IN = W - M * 2;

  const S0 = form.sinistro || {};
  const A = form.A || {};
  const B = form.B || {};
  const alt = form.altre || {};
  const circ = form.circostanze || CIRCOSTANZE.map((t) => ({ t, a: false, b: false }));

  const box = (x, y, w, h) => { doc.setDrawColor(120); doc.setLineWidth(0.2); doc.rect(x, y, w, h); };
  const cbX = (x, y, checked, s = 2.6) => {
    doc.setDrawColor(70); doc.setLineWidth(0.25); doc.rect(x, y, s, s);
    if (checked) { doc.setLineWidth(0.4); doc.line(x + 0.4, y + 0.4, x + s - 0.4, y + s - 0.4); doc.line(x + s - 0.4, y + 0.4, x + 0.4, y + s - 0.4); }
  };
  const fld = (x, yb, w, label, value, o = {}) => {
    let vx = x;
    if (label) {
      doc.setFont("helvetica", "bold"); doc.setFontSize(o.ls || 6.8); doc.setTextColor(0);
      doc.text(label, x, yb);
      vx = x + doc.getTextWidth(label) + 1.5;
    }
    doc.setDrawColor(150); doc.setLineWidth(0.15);
    doc.line(vx, yb + 0.7, x + w, yb + 0.7);
    if (value != null && String(value) !== "") {
      doc.setFont(o.mono ? "courier" : "helvetica", "normal"); doc.setFontSize(7.3); doc.setTextColor(0);
      doc.text(doc.splitTextToSize(val(value), Math.max(4, x + w - vx - 0.5))[0] || "", vx + 0.6, yb);
    }
  };
  const sh = (x, yb, text, note, w) => {
    doc.setFont("helvetica", "bold"); doc.setFontSize(7.2); doc.setTextColor(10);
    doc.text(text, x, yb);
    if (note) {
      const tw = doc.getTextWidth(text);
      doc.setFont("helvetica", "normal"); doc.setFontSize(4);
      const nw = doc.getTextWidth(note);
      if (!w || tw + nw + 2 <= w) { doc.setTextColor(60); doc.text(note, x + tw + 1.4, yb); }
    }
  };

  // ===== Intestazione =====
  let y = M + 1;
  if (withHeader) {
    doc.setFont("helvetica", "bold"); doc.setFontSize(8.5); doc.setTextColor(20);
    doc.text(val(studio.name || studio.studio_name || "Studio"), M, y + 1);
  }
  doc.setFont("helvetica", "bold"); doc.setFontSize(7.5); doc.setTextColor(20);
  doc.text(`Pratica N. ${val(form.numero_pratica)}`, W - M, y + 1, { align: "right" });
  doc.setFont("helvetica", "bold"); doc.setFontSize(13); doc.setTextColor(15);
  doc.text("Constatazione amichevole di incidente - Denuncia di sinistro", W / 2, y + 6.5, { align: "center" });
  doc.setFont("helvetica", "normal"); doc.setFontSize(5.2); doc.setTextColor(70);
  doc.text("(art. 5 D.L. n. 857 del 1976 convertito legge n. 39 del 1977)  —  Se firmato congiuntamente produce gli effetti di cui agli artt. 3 e 5 D.L. n. 857/1976.", W / 2, y + 10, { align: "center" });
  y += 12;

  // ===== Riga superiore full-width =====
  const b1h = 8;
  box(M, y, 44, b1h);
  doc.setFont("helvetica", "bold"); doc.setFontSize(6.2); doc.setTextColor(20);
  doc.text("1. data incidente", M + 1, y + 2.6);
  doc.setFont("helvetica", "normal"); doc.setFontSize(6.4); doc.setTextColor(10);
  doc.text(val(S0.data), M + 1, y + 6.2);
  box(M + 44, y, 18, b1h);
  doc.setFont("helvetica", "bold"); doc.setFontSize(6.2); doc.setTextColor(20);
  doc.text("ora", M + 45, y + 2.6);
  doc.setFont("helvetica", "normal"); doc.setTextColor(10); doc.text(val(S0.ora), M + 45, y + 6.2);
  box(M + 62, y, 82, b1h);
  doc.setFont("helvetica", "bold"); doc.setFontSize(6.2); doc.setTextColor(20);
  doc.text("2. luogo (comune, provincia, via e numero)", M + 63, y + 2.6);
  doc.setFont("helvetica", "normal"); doc.setTextColor(10);
  doc.text(doc.splitTextToSize(val(S0.luogo), 80)[0] || "", M + 63, y + 6.2);
  box(M + 144, y, IN - 144, b1h);
  doc.setFont("helvetica", "bold"); doc.setFontSize(6.2); doc.setTextColor(20);
  doc.text("3. feriti anche se lievi", M + 145, y + 2.6);
  doc.setFontSize(6); doc.text("no", M + 145, y + 6.4); cbX(M + 150, y + 4.2, S0.feriti === "no");
  doc.text("sì", M + 158, y + 6.4); cbX(M + 162, y + 4.2, S0.feriti === "si");
  y += b1h;
  const b2h = 12;
  box(M, y, 62, b2h);
  doc.setFont("helvetica", "bold"); doc.setFontSize(6.2); doc.setTextColor(20);
  doc.text("4. danni materiali", M + 1, y + 2.8);
  doc.setFont("helvetica", "normal"); doc.setFontSize(5.6); doc.setTextColor(30);
  doc.text("veicoli oltre A o B", M + 1, y + 6.4); cbX(M + 27, y + 4.65, S0.danni_veicoli === "no"); doc.text("no", M + 30, y + 6.4); cbX(M + 35, y + 4.65, S0.danni_veicoli === "si"); doc.text("sì", M + 38, y + 6.4);
  doc.text("oggetti diversi dai veicoli", M + 1, y + 10); cbX(M + 34, y + 8.25, S0.danni_oggetti === "no"); doc.text("no", M + 37, y + 10); cbX(M + 42, y + 8.25, S0.danni_oggetti === "si"); doc.text("sì", M + 45, y + 10);
  box(M + 62, y, IN - 62, b2h);
  doc.setFont("helvetica", "bold"); doc.setFontSize(6.2); doc.setTextColor(20);
  doc.text("5. testimoni: cognome, nome, indirizzo, telefono", M + 63, y + 2.8);
  doc.setFont("helvetica", "normal"); doc.setFontSize(5); doc.setTextColor(70);
  doc.text("(precisare se si tratta di trasportati sul veicolo A o B)", M + 63, y + 5.6);
  doc.setFont("helvetica", "normal"); doc.setFontSize(6); doc.setTextColor(10);
  const testLines = doc.splitTextToSize(val(S0.testimoni), IN - 66);
  doc.setDrawColor(150); doc.setLineWidth(0.15);
  doc.line(M + 63, y + 9.2, M + IN - 1, y + 9.2);
  doc.line(M + 63, y + 11.4, M + IN - 1, y + 11.4);
  if (testLines[0]) doc.text(testLines[0], M + 63, y + 8.8);
  if (testLines[1]) doc.text(testLines[1], M + 63, y + 11);
  y += b2h + 1;

  // ===== Geometria tre colonne =====
  const colTop = y;
  const xA = M, wA = 59;
  const cbAx = xA + wA + 0.6;
  const xC = xA + wA + 5, wC = 66;
  const cbBx = xC + wC + 1.4;
  const xB = xC + wC + 5, wB = 59;
  const firmeTop = 258;
  const colBodyH = firmeTop - colTop - 6;
  const hBar = 6;
  doc.setFillColor(...CYAN); doc.rect(xA, colTop, wA + 4.4, hBar, "F");
  doc.setFillColor(...YELLOW); doc.rect(xB - 4.4, colTop, wB + 4.4, hBar, "F");
  doc.setFillColor(...CYAN_T); doc.rect(xA, colTop + hBar, wA + 4.4, colBodyH + 6, "F");
  doc.setFillColor(...YELLOW_T); doc.rect(xB - 4.4, colTop + hBar, wB + 4.4, colBodyH + 6, "F");
  doc.setFont("helvetica", "bold"); doc.setTextColor(20);
  doc.setFontSize(9); doc.text("veicolo", xA + (wA + 4.4) / 2 - 3, colTop + 4.3, { align: "right" });
  doc.setFontSize(13); doc.text("A", xA + (wA + 4.4) / 2 + 1, colTop + 4.7);
  doc.setFontSize(9); doc.text("veicolo", (xB - 4.4) + (wB + 4.4) / 2 - 3, colTop + 4.3, { align: "right" });
  doc.setFontSize(13); doc.text("B", (xB - 4.4) + (wB + 4.4) / 2 + 1, colTop + 4.7);
  const bodyTop = colTop + hBar + 4;

  const drawVehicle = (x, w, S) => {
    const R = 4.55, half = w / 2;
    let cy = bodyTop;
    sh(x, cy, "6. contraente/assicurato", "(vedere certificato d'assicurazione)", w); cy += R;
    fld(x, cy, w, "Cognome", val(S.cognome).toUpperCase()); cy += R;
    fld(x, cy, w, "Nome", S.nome); cy += R;
    fld(x, cy, w, "Codice Fiscale / Partita IVA", S.cf, { mono: true }); cy += R;
    fld(x, cy, w, "Indirizzo (comune, provincia, via e numero)", ""); cy += R;
    fld(x, cy, w, "", S.indirizzo); cy += R;
    fld(x, cy, half, "C.A.P.", S.cap, { mono: true }); fld(x + half, cy, half, "Stato", S.stato); cy += R;
    fld(x, cy, w, "N. Tel. o E-mail", S.tel); cy += R + 0.5;
    sh(x, cy, "7. veicolo"); cy += 1.4;
    const c2 = w / 2;
    doc.setDrawColor(120); doc.setLineWidth(0.15);
    doc.rect(x, cy, c2, 3.4); doc.rect(x + c2, cy, c2, 3.4);
    doc.setFont("helvetica", "bold"); doc.setFontSize(5.4); doc.setTextColor(30);
    doc.text("A MOTORE", x + c2 / 2, cy + 2.4, { align: "center" });
    doc.text("RIMORCHIO", x + c2 + c2 / 2, cy + 2.4, { align: "center" });
    cy += 3.4;
    [["Marca, Tipo", S.marca], ["N. di targa o telaio", S.targa], ["Stato d'immatricolazione", S.stato_imm]].forEach(([lab, va]) => {
      doc.rect(x, cy, c2, 6); doc.rect(x + c2, cy, c2, 6);
      doc.setFont("helvetica", "normal"); doc.setFontSize(5.6); doc.setTextColor(60);
      doc.text(lab, x + 0.8, cy + 2.2); doc.text(lab, x + c2 + 0.8, cy + 2.2);
      doc.setFontSize(7); doc.setTextColor(0);
      doc.text(doc.splitTextToSize(val(va), c2 - 1.6)[0] || "", x + 0.8, cy + 5);
      cy += 6;
    });
    cy += 1.5;
    sh(x, cy, "8. compagnia d'assicurazione", "(vedere cert.)", w); cy += R;
    fld(x, cy, w, "Denominazione", S.ass_denominazione); cy += R;
    fld(x, cy, w, "N. di polizza", S.polizza, { mono: true }); cy += R;
    fld(x, cy, w, "N. di Carta Verde", S.carta_verde, { mono: true }); cy += R;
    doc.setFont("helvetica", "bold"); doc.setFontSize(5.6); doc.setTextColor(20);
    doc.text("Certificato di assicurazione o Carta Verde", x, cy); cy += 3.4;
    fld(x, cy, half, "Valido dal", S.valido_dal, { mono: true }); fld(x + half, cy, half, "al", S.valido_al, { mono: true }); cy += R;
    fld(x, cy, w, "Agenzia (o ufficio o intermediario)", S.agenzia); cy += R;
    fld(x, cy, w, "Indirizzo", S.ag_indirizzo); cy += R;
    fld(x, cy, half, "Stato", S.ag_stato); fld(x + half, cy, half, "N. Tel. o E-mail", S.ag_tel); cy += R;
    doc.setFont("helvetica", "normal"); doc.setFontSize(5.4); doc.setTextColor(20);
    doc.text("La polizza copre anche i danni materiali al proprio veicolo:", x, cy);
    cbX(x + 47, cy - 2.2, S.polizza_copre === "no"); doc.text("no", x + 50.4, cy); cbX(x + 54, cy - 2.2, S.polizza_copre === "si"); doc.text("sì", x + 57.4, cy);
    cy += R + 1;
    sh(x, cy, "9. conducente", "(vedere patente di guida)", w); cy += R;
    fld(x, cy, w, "Cognome", val(S.cond_cognome).toUpperCase()); cy += R;
    fld(x, cy, w, "Nome", S.cond_nome); cy += R;
    fld(x, cy, w, "Data di nascita", S.cond_nascita, { mono: true }); cy += R;
    fld(x, cy, w, "Codice Fiscale", S.cond_cf, { mono: true }); cy += R;
    fld(x, cy, w, "Indirizzo (comune, provincia, via e numero)", ""); cy += R;
    fld(x, cy, w, "", S.cond_indirizzo); cy += R;
    fld(x, cy, half, "Stato", S.cond_stato); fld(x + half, cy, half, "N. Tel. o E-mail", S.cond_tel); cy += R;
    fld(x, cy, w, "Patente N.", S.patente, { mono: true }); cy += R;
    fld(x, cy, half * 0.62, "Categoria", S.categoria); fld(x + half * 0.62, cy, w - half * 0.62, "valida fino al", S.patente_scad, { mono: true }); cy += R + 1;
    sh(x, cy, "10. punto d'urto iniziale del veicolo (freccia +)"); cy += 2;
    box(x, cy, w, 18);
    // Sagoma auto (vista dall'alto, anteriore in alto) sulla metà sinistra.
    const carW = 9, carH = 14, carX = x + w * 0.3 - carW / 2, carY = cy + 2;
    doc.setDrawColor(90); doc.setLineWidth(0.3);
    doc.roundedRect(carX, carY, carW, carH, 3, 3);
    // Parabrezza (anteriore, in alto): trapezio con lato corto verso il cofano → davanti riconoscibile.
    const wTop = carY + carH * 0.16, wBot = carY + carH * 0.34;
    doc.setLineWidth(0.25);
    doc.line(carX + carW * 0.36, wTop, carX + carW * 0.64, wTop);
    doc.line(carX + carW * 0.22, wBot, carX + carW * 0.78, wBot);
    doc.line(carX + carW * 0.36, wTop, carX + carW * 0.22, wBot);
    doc.line(carX + carW * 0.64, wTop, carX + carW * 0.78, wBot);
    // Lunotto posteriore.
    doc.line(carX + carW * 0.26, carY + carH * 0.76, carX + carW * 0.74, carY + carH * 0.76);
    // Sagoma moto/ciclomotore (vista dall'alto, anteriore in alto) sulla metà destra.
    const mW = 5.5, mH = 14, mX = x + w * 0.7, mY = cy + 2; // mX = centro orizzontale della moto
    doc.setDrawColor(90);
    doc.setFillColor(120, 120, 120);
    // Ruota anteriore (in alto).
    doc.setLineWidth(0.25);
    doc.roundedRect(mX - mW * 0.22, mY, mW * 0.44, mH * 0.14, 0.4, 0.4, "FD");
    // Manubrio (anteriore): barra trasversale larga → davanti riconoscibile.
    doc.setLineWidth(0.4);
    doc.line(mX - mW * 0.72, mY + mH * 0.17, mX + mW * 0.72, mY + mH * 0.17);
    // Corpo affusolato (serbatoio/sella).
    doc.setLineWidth(0.3);
    doc.roundedRect(mX - mW / 2, mY + mH * 0.2, mW, mH * 0.6, 1.4, 1.4);
    // Ruota posteriore (in basso).
    doc.setLineWidth(0.25);
    doc.roundedRect(mX - mW * 0.22, mY + mH * 0.86, mW * 0.44, mH * 0.14, 0.4, 0.4, "FD");
    // Punto d'urto iniziale selezionato (croce rossa): supporta più punti sulle sagome.
    const urtoPts = Array.isArray(S.urto) ? S.urto : (S.urto && typeof S.urto.x === "number" ? [S.urto] : []);
    if (urtoPts.length) {
      doc.setDrawColor(220, 20, 20); doc.setLineWidth(0.6);
      urtoPts.forEach((p) => {
        if (!p || typeof p.x !== "number") return;
        const ux = x + Math.max(0, Math.min(1, p.x)) * w, uy = cy + Math.max(0, Math.min(1, p.y)) * 18;
        doc.line(ux - 2.3, uy, ux + 2.3, uy); doc.line(ux, uy - 2.3, ux, uy + 2.3);
      });
    }
    cy += 18 + 2;
    sh(x, cy, "11. danni visibili al veicolo"); cy += R;
    const dv = doc.splitTextToSize(val(S.danni_visibili), w);
    fld(x, cy, w, "", dv[0] || ""); cy += R; fld(x, cy, w, "", dv[1] || ""); cy += R + 1;
    sh(x, cy, "14. osservazioni"); cy += R;
    const ob = doc.splitTextToSize(val(S.osservazioni), w);
    fld(x, cy, w, "", ob[0] || ""); cy += R;
    if (ob[1]) { fld(x, cy, w, "", ob[1]); }
  };
  drawVehicle(xA, wA, A);
  drawVehicle(xB, wB, B);

  // ===== Colonna centrale: 12 circostanze =====
  doc.setFont("helvetica", "bold"); doc.setFontSize(7.2); doc.setTextColor(15);
  doc.text("12. circostanze dell'incidente", xC + wC / 2, bodyTop, { align: "center" });
  doc.setFont("helvetica", "bold"); doc.setFontSize(5.6); doc.setTextColor(40);
  doc.text("Mettere una croce (X) soltanto nelle caselle utili alla descrizione", xC + wC / 2, bodyTop + 3.2, { align: "center" });
  doc.setFont("helvetica", "normal"); doc.setFontSize(4.8); doc.setTextColor(80);
  doc.text("(cancellare l'opzione non corretta)", xC + wC / 2, bodyTop + 6, { align: "center" });
  let ccy = bodyTop + 10;
  circ.forEach((c, i) => {
    const lines = doc.splitTextToSize(c.t, wC - 12);
    const rowH = Math.max(5, lines.length * 2.5 + 1.8);
    const midY = ccy + rowH / 2;
    cbX(cbAx, midY - 1.3, c.a, 2.6);
    cbX(cbBx, midY - 1.3, c.b, 2.6);
    doc.setFont("helvetica", "normal"); doc.setFontSize(6.2); doc.setTextColor(10);
    doc.text(String(i + 1), xC + 2, midY + 0.6);
    doc.text(String(i + 1), xC + wC - 2, midY + 0.6, { align: "right" });
    doc.text(lines, xC + wC / 2, midY - (lines.length - 1) * 1.2 + 0.6, { align: "center" });
    ccy += rowH;
  });
  doc.setLineWidth(0.3); doc.setDrawColor(20);
  doc.line(xC + 6, ccy + 2, xC + 1, ccy + 2); doc.line(xC + 1, ccy + 2, xC + 3, ccy + 0.8); doc.line(xC + 1, ccy + 2, xC + 3, ccy + 3.2);
  doc.line(xC + wC - 6, ccy + 2, xC + wC - 1, ccy + 2); doc.line(xC + wC - 1, ccy + 2, xC + wC - 3, ccy + 0.8); doc.line(xC + wC - 1, ccy + 2, xC + wC - 3, ccy + 3.2);
  doc.setFont("helvetica", "bold"); doc.setFontSize(5.6); doc.setTextColor(20);
  doc.text("indicare il numero di caselle barrate con una croce", xC + wC / 2, ccy + 2.6, { align: "center" });
  // Totali caselle barrate: quadratino con il numero accanto alla freccia sinistra (Veicolo A) e destra (Veicolo B).
  const aCount = circ.filter((c) => c.a).length;
  const bCount = circ.filter((c) => c.b).length;
  const tbW = 4.4, tbH = 4, tbY = ccy;
  doc.setDrawColor(20); doc.setLineWidth(0.3);
  doc.setFillColor(255, 255, 255);
  doc.rect(xC - tbW, tbY, tbW, tbH, "FD");
  doc.rect(xC + wC, tbY, tbW, tbH, "FD");
  doc.setFont("helvetica", "bold"); doc.setFontSize(7); doc.setTextColor(10);
  doc.text(String(aCount), xC - tbW / 2, tbY + tbH / 2 + 1, { align: "center" });
  doc.text(String(bCount), xC + wC + tbW / 2, tbY + tbH / 2 + 1, { align: "center" });
  ccy += 6;
  doc.setFont("helvetica", "italic"); doc.setFontSize(4.8); doc.setTextColor(90);
  doc.text(doc.splitTextToSize("Il presente documento non costituisce un'ammissione di responsabilità bensì una mera rilevazione dell'identità delle persone e dei fatti, per una rapida definizione.", wC), xC, ccy);
  ccy += 8;
  doc.setFont("helvetica", "bold"); doc.setFontSize(6.6); doc.setTextColor(15);
  doc.text("13. grafico dell'incidente al momento dell'urto", xC + wC / 2, ccy, { align: "center" });
  ccy += 2;
  const gridBottom = firmeTop - 2;
  const gridH = gridBottom - ccy;
  box(xC, ccy, wC, gridH);
  doc.setFont("helvetica", "normal"); doc.setFontSize(4.4); doc.setTextColor(90);
  doc.text(doc.splitTextToSize("Indicare: 1) il tracciato delle strade; 2) la direzione di marcia di A e B; 3) la loro posizione al momento dell'urto; 4) i segnali stradali; 5) i nomi delle strade.", wC - 3), xC + 1.5, ccy + 3);
  // Griglia di sfondo: disegnata PRIMA del testo della dinamica così resta sotto.
  doc.setDrawColor(215); doc.setLineWidth(0.1);
  for (let gx = xC + 4; gx < xC + wC; gx += 4) doc.line(gx, ccy + 8, gx, ccy + gridH - 1);
  for (let gy = ccy + 10; gy < ccy + gridH; gy += 4) doc.line(xC + 1, gy, xC + wC - 1, gy);
  // Testo della dinamica in primo piano, con fondo bianco per garantire la leggibilità sopra la griglia.
  if (form.dinamica) {
    const dinPt = Math.max(10, Math.min(18, Number(form.dinamica_font) || 14));
    const maxLines = dinPt <= 12 ? 6 : dinPt <= 15 ? 5 : 4;
    // Spazio disponibile = righe massime alla dimensione scelta; se il testo non entra, il carattere
    // si riduce (fino a 7pt) così la dinamica è sempre mostrata per intero (come nell'anteprima del grafico).
    const availH = maxLines * dinPt * 0.5 * 0.3528 * 1.15;
    doc.setFont("helvetica", "italic");
    let fs = dinPt * 0.5, dinLines = [];
    for (let pt = dinPt; pt >= 7; pt -= 0.5) {
      fs = pt * 0.5; doc.setFontSize(fs);
      dinLines = doc.splitTextToSize(val(form.dinamica), wC - 3);
      if (dinLines.length * fs * 0.3528 * 1.15 <= availH + 0.01) break;
    }
    const lineH = fs * 0.3528 * 1.15;
    const txtTop = ccy + 8;
    doc.setFillColor(255, 255, 255);
    doc.rect(xC + 1, txtTop - lineH, wC - 2, dinLines.length * lineH + 1, "F");
    doc.setTextColor(35);
    doc.text(dinLines, xC + 1.5, txtTop);
  }
  if (form.croquis) { try { doc.addImage(form.croquis, "PNG", xC, ccy, wC, gridH); } catch (e) { /* immagine non valida */ } }
  doc.setDrawColor(60); doc.setLineWidth(0.3);
  doc.line(xC + wC - 6, ccy + gridH - 4, xC + wC - 6, ccy + gridH - 11);
  doc.line(xC + wC - 6, ccy + gridH - 11, xC + wC - 7.2, ccy + gridH - 9); doc.line(xC + wC - 6, ccy + gridH - 11, xC + wC - 4.8, ccy + gridH - 9);
  doc.setFont("helvetica", "bold"); doc.setFontSize(5); doc.setTextColor(30);
  doc.text("N", xC + wC - 6, ccy + gridH - 12, { align: "center" });

  // ===== 15. firma =====
  doc.setFont("helvetica", "bold"); doc.setFontSize(6.4); doc.setTextColor(15);
  doc.text("15. firma dei conducenti", xC + wC / 2, firmeTop + 2, { align: "center" });
  box(xC, firmeTop + 3, wC / 2 - 1, 14); box(xC + wC / 2 + 1, firmeTop + 3, wC / 2 - 1, 14);
  // Lettere A/B piccole negli angoli superiori: A in alto a sinistra del riquadro A, B in alto a destra del riquadro B.
  doc.setFont("helvetica", "bold"); doc.setFontSize(5); doc.setTextColor(40);
  doc.text("A", xC + 1.5, firmeTop + 6);
  doc.text("B", xC + wC - 1.5, firmeTop + 6, { align: "right" });

  doc.setFont("helvetica", "italic"); doc.setFontSize(4.6); doc.setTextColor(90);
  doc.text("* In caso di lesioni o danni a cose diverse dai veicoli A e B indicare, sulla denuncia a tergo, l'identità e l'indirizzo dei danneggiati.", M, H - 6, { maxWidth: wA + 4 });
  doc.text("La denuncia non deve essere modificata dopo la firma e la separazione degli esemplari.", xC, H - 6, { maxWidth: wC });
  doc.text("Vedere a tergo le dichiarazioni dell'Assicurato.", xB - 4, H - 6, { maxWidth: wB + 4 });

  // ===================== PAGINA 2 — Altre informazioni =====================
  doc.addPage();
  let y2 = M + 2;
  doc.setFont("helvetica", "bold"); doc.setFontSize(14); doc.setTextColor(15);
  doc.text("Altre informazioni", M, y2 + 2);
  doc.setFont("helvetica", "normal"); doc.setFontSize(5.2); doc.setTextColor(70);
  doc.text(doc.splitTextToSize("richieste ai sensi dell'art. 2, comma 5 quater, della Legge 26 maggio 2000 n. 137 e successivo provvedimento Isvap n. 01764 del 21 dicembre 2000.", 110), M + 42, y2 - 1);
  y2 += 8;
  doc.setFont("helvetica", "normal"); doc.setFontSize(7); doc.setTextColor(20);
  doc.text("C'è stato intervento dell'Autorità?", M, y2 + 2);
  doc.setFontSize(6.2); doc.text("sì", M + 46, y2 + 2); cbX(M + 49, y2 - 0.4, alt.autorita === "si", 3); doc.text("no", M + 58, y2 + 2); cbX(M + 62, y2 - 0.4, alt.autorita === "no", 3);
  doc.setFontSize(7); doc.text("Quale Autorità è intervenuta?", M + 90, y2 + 2);
  doc.setFontSize(6.2);
  doc.text("CC", M + 132, y2 + 2); cbX(M + 138, y2 - 0.4, alt.cc, 3);
  doc.text("P.S.", M + 148, y2 + 2); cbX(M + 155, y2 - 0.4, alt.ps, 3);
  doc.text("VV.UU.", M + 166, y2 + 2); cbX(M + 177, y2 - 0.4, alt.vvuu, 3);
  y2 += 6;
  doc.setFont("helvetica", "normal"); doc.setFontSize(6.4); doc.setTextColor(20);
  doc.text("Applicazione Legge 39/2000 (indennizzo diretto):", M, y2 + 2);
  doc.text("sì", M + 62, y2 + 2); cbX(M + 65, y2 - 0.2, alt.legge39 === "si"); doc.text("no", M + 73, y2 + 2); cbX(M + 76, y2 - 0.2, alt.legge39 === "no");
  y2 += 5;

  const colW2 = (IN - 4) / 2;
  const x2A = M, x2B = M + colW2 + 4;
  doc.setFillColor(...CYAN); doc.rect(x2A, y2, colW2, 6, "F");
  doc.setFillColor(...YELLOW); doc.rect(x2B, y2, colW2, 6, "F");
  doc.setFont("helvetica", "bold"); doc.setTextColor(20);
  doc.setFontSize(9); doc.text("veicolo", x2A + colW2 / 2 - 3, y2 + 4.3, { align: "right" });
  doc.setFontSize(12); doc.text("A", x2A + colW2 / 2 + 1, y2 + 4.7);
  doc.setFontSize(9); doc.text("veicolo", x2B + colW2 / 2 - 3, y2 + 4.3, { align: "right" });
  doc.setFontSize(12); doc.text("B", x2B + colW2 / 2 + 1, y2 + 4.7);
  const body2Top = y2 + 6;
  doc.setFillColor(...CYAN_T); doc.rect(x2A, body2Top, colW2, H - M - body2Top, "F");
  doc.setFillColor(...YELLOW_T); doc.rect(x2B, body2Top, colW2, H - M - body2Top, "F");

  const drawAltre = (x, w, S) => {
    const R = 5.2, half = w / 2;
    const F2 = S.foglio2 || {};
    const tst = F2.testimoni || [], pr = F2.proprietario || {}, fer = F2.feriti || [];
    let cy = body2Top + 5;
    fld(x, cy, w, "TARGA", S.targa, { ls: 7, mono: true }); cy += R + 1;
    [0, 1].forEach((i) => {
      const t = tst[i] || {};
      doc.setFont("helvetica", "bold"); doc.setFontSize(6.4); doc.setTextColor(20); doc.text("Testimone", x, cy); cy += R;
      fld(x, cy, w, "Nome e cognome", t.nome); cy += R;
      fld(x, cy, w, "Codice fiscale", t.cf, { mono: true }); cy += R;
      fld(x, cy, w * 0.6, "Indirizzo", t.indirizzo); fld(x + w * 0.6, cy, w * 0.4, "Tel.", t.tel, { mono: true }); cy += R + 1;
    });
    doc.setFont("helvetica", "bold"); doc.setFontSize(6.4); doc.setTextColor(20);
    doc.text("Proprietario (solo se diverso dal Contraente/Assicurato)", x, cy); cy += R;
    fld(x, cy, w, "Nome e cognome o Denominazione sociale", pr.nome); cy += R;
    fld(x, cy, w, "Indirizzo (Comune, via e numero)", pr.indirizzo); cy += R;
    fld(x, cy, half, "C.A.P.", pr.cap); fld(x + half, cy, half, "Provincia", pr.provincia); cy += R;
    fld(x, cy, w, "Luogo e data di nascita", pr.nascita); cy += R;
    fld(x, cy, w, "Codice fiscale/Partita IVA", pr.cf, { mono: true }); cy += R + 1;
    [0, 1].forEach((i) => {
      const f = fer[i] || {};
      doc.setFont("helvetica", "bold"); doc.setFontSize(6.2); doc.setTextColor(20);
      doc.text("Ferito", x, cy); doc.setFont("helvetica", "normal"); doc.setFontSize(4.8); doc.text("(Barrare solo una casella)", x + 8, cy);
      cy += R;
      doc.setFont("helvetica", "bold"); doc.setFontSize(6); doc.setTextColor(20);
      doc.text("Conducente", x, cy); cbX(x + 18, cy - 2.6, f.tipo === "conducente", 3);
      doc.text("Passeggero", x + 26, cy); cbX(x + 44, cy - 2.6, f.tipo === "passeggero", 3);
      doc.text("Pedone", x + 52, cy); cbX(x + 65, cy - 2.6, f.tipo === "pedone", 3);
      cy += R;
      fld(x, cy, w, "Nome e cognome", f.nome); cy += R;
      fld(x, cy, w, "Indirizzo (Comune, via e numero)", f.indirizzo); cy += R;
      fld(x, cy, w, "Luogo e data di nascita", f.nascita); cy += R;
      fld(x, cy, w, "Codice fiscale", f.cf, { mono: true }); cy += R;
      doc.setFont("helvetica", "normal"); doc.setFontSize(6); doc.setTextColor(20);
      doc.text("Ricovero Pronto Soccorso", x, cy); doc.text("sì", x + 40, cy); cbX(x + 43, cy - 2.2, f.ricovero === "si"); doc.text("no", x + 50, cy); cbX(x + 53, cy - 2.2, f.ricovero === "no"); cy += R;
      fld(x, cy, w, "Nome Pronto Soccorso / Comune", f.ps); cy += R + 1;
    });
  };
  drawAltre(x2A, colW2, A);
  drawAltre(x2B, colW2, B);

  const filename = `CAI_UFFICIALE_${val(form.numero_pratica || "pratica").replace(/\//g, "-")}.pdf`;
  return { doc, filename };
}

// Modello compatto su misura (A4 una pagina), generato dal FORM editabile.
export function buildCaiCid(form = {}, studio = {}, opts = {}) {
  const withHeader = opts.intestazione !== false;
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const W = 210, M = 12, IN = W - M * 2;
  let y = M;
  const S0 = form.sinistro || {}, A = form.A || {}, B = form.B || {};
  const mk = (v, on) => (v === on ? "X" : " ");
  const cell = (x, cy, w, h, label, value, o = {}) => {
    doc.setDrawColor(150); doc.setLineWidth(0.2); doc.rect(x, cy, w, h);
    doc.setFont("helvetica", "normal"); doc.setFontSize(6); doc.setTextColor(120);
    doc.text(String(label).toUpperCase(), x + 1.3, cy + 2.7);
    doc.setFont(o.mono ? "courier" : "helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(20);
    doc.text(doc.splitTextToSize(val(value), w - 2.6).slice(0, Math.max(1, Math.floor((h - 3) / 3.6))), x + 1.3, cy + 6.2);
  };
  const bar = (cy, text) => {
    doc.setFillColor(37, 99, 235); doc.rect(M, cy, IN, 6, "F");
    doc.setFont("helvetica", "bold"); doc.setFontSize(8.5); doc.setTextColor(255);
    doc.text(String(text).toUpperCase(), M + 2, cy + 4.1); doc.setTextColor(20); return cy + 6;
  };
  if (withHeader) {
    doc.setFont("helvetica", "bold"); doc.setFontSize(13); doc.setTextColor(20);
    doc.text(val(studio.name || studio.studio_name || "Studio"), M, y + 3);
    doc.setFont("helvetica", "normal"); doc.setFontSize(7.5); doc.setTextColor(100);
    const c1 = join([studio.indirizzo, join([studio.cap, studio.comune]), studio.provincia && `(${studio.provincia})`]);
    if (c1) doc.text(c1, M, y + 7.5);
    y += 12;
  }
  doc.setFont("helvetica", "bold"); doc.setFontSize(8); doc.setTextColor(20);
  doc.text(`Pratica N. ${val(form.numero_pratica)}`, W - M, y + 3, { align: "right" });
  y += 4;
  doc.setFillColor(30, 58, 138); doc.rect(M, y, IN, 8, "F");
  doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(255);
  doc.text("CONSTATAZIONE AMICHEVOLE DI INCIDENTE (C.A.I. / C.I.D.)", W / 2, y + 5.4, { align: "center" });
  doc.setTextColor(20); y += 8;

  y = bar(y, "Dati del sinistro");
  const c4 = IN / 4;
  cell(M, y, c4, 9, "Data sinistro", S0.data, { mono: true });
  cell(M + c4, y, c4, 9, "Ora sinistro", S0.ora, { mono: true });
  cell(M + c4 * 2, y, c4, 9, "Feriti", `SI [${mk(S0.feriti, "si")}]   NO [${mk(S0.feriti, "no")}]`);
  cell(M + c4 * 3, y, c4, 9, "Danni a cose (oltre ai veicoli)", `SI [${mk(S0.danni_oggetti, "si")}]   NO [${mk(S0.danni_oggetti, "no")}]`);
  y += 9;
  cell(M, y, IN, 9, "Comune e indirizzo del sinistro", S0.luogo);
  y += 9 + 2;

  const colW = (IN - 2) / 2, xA = M, xB = M + colW + 2;
  doc.setFillColor(22, 101, 52); doc.rect(xA, y, colW, 6, "F");
  doc.setFillColor(146, 64, 14); doc.rect(xB, y, colW, 6, "F");
  doc.setFont("helvetica", "bold"); doc.setFontSize(8.5); doc.setTextColor(255);
  doc.text("VEICOLO A  (Assistito)", xA + 2, y + 4.1);
  doc.text("VEICOLO B  (Controparte)", xB + 2, y + 4.1);
  doc.setTextColor(20); y += 6;

  const rows = [
    ["Cognome e nome", join([A.cognome, A.nome]), join([B.cognome, B.nome]), 9],
    ["Codice fiscale / P.IVA", A.cf, B.cf, 8],
    ["Indirizzo", A.indirizzo, B.indirizzo, 10],
    ["Patente n. / categoria", join([A.patente, A.categoria]), join([B.patente, B.categoria]), 8],
    ["Assicurazione (compagnia)", A.ass_denominazione, B.ass_denominazione, 8],
    ["Polizza / scadenza", join([A.polizza, A.valido_al]), join([B.polizza, B.valido_al]), 8],
    ["Targa / marca e modello", join([A.targa, A.marca]), join([B.targa, B.marca]), 8],
  ];
  rows.forEach(([label, va, vb, h]) => {
    cell(xA, y, colW, h, label, va, { mono: /targa|polizza|patente/i.test(label) });
    cell(xB, y, colW, h, label, vb, { mono: /targa|polizza|patente/i.test(label) });
    y += h;
  });
  y += 2;

  y = bar(y, "Circostanze / dinamica del sinistro");
  const dinamica = val(form.dinamica);
  const dinScale = Math.max(10, Math.min(18, Number(form.dinamica_font) || 14)) / 14;
  doc.setFont("helvetica", "normal"); doc.setFontSize(8.5 * dinScale); doc.setTextColor(20);
  const dinLines = doc.splitTextToSize(dinamica, IN - 4);
  const dinLineH = 3.6 * dinScale;
  const dinH = Math.max(16, Math.min(dinLines.length * dinLineH + 6, 70));
  doc.setDrawColor(150); doc.rect(M, y, IN, dinH);
  doc.text(dinLines, M + 1.5, y + 2 + dinLineH); y += dinH + 2;

  const croqH = 32;
  doc.setDrawColor(150); doc.rect(M, y, IN, croqH);
  doc.setFont("helvetica", "normal"); doc.setFontSize(6); doc.setTextColor(120);
  doc.text("CROQUIS (schema grafico dell'incidente)", M + 1.3, y + 2.7);
  if (form.croquis) { try { doc.addImage(form.croquis, "PNG", M, y, IN, croqH); } catch (e) { /* immagine non valida */ } }
  doc.setTextColor(20); y += croqH + 2;

  y = bar(y, "Danni dichiarati");
  cell(xA, y, colW, 14, "Danni veicolo A", A.danni_visibili);
  cell(xB, y, colW, 14, "Danni veicolo B", B.danni_visibili);
  y += 14 + 4;

  doc.setDrawColor(150); doc.rect(xA, y, colW, 16); doc.rect(xB, y, colW, 16);
  doc.setFont("helvetica", "normal"); doc.setFontSize(6); doc.setTextColor(120);
  doc.text("FIRMA CONDUCENTE A", xA + 1.3, y + 2.7);
  doc.text("FIRMA CONDUCENTE B", xB + 1.3, y + 2.7);
  doc.setTextColor(20);

  const filename = `CAI_${val(form.numero_pratica || "pratica").replace(/\//g, "-")}.pdf`;
  return { doc, filename };
}
